import {
  findDuplicates,
  formatAddress,
  type Contact,
  type Job,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { supabase } from "../../lib/supabase";

interface PublicLead {
  contact_id: string | null;
  customer_match_reviewed_at: string | null;
}

/** A public form is a claim, not proof of identity. Staff makes the link. */
export function PublicLeadMatchReview({
  job,
  contacts,
  onLinked,
}: {
  job: Job;
  contacts: readonly Contact[];
  onLinked: () => Promise<void>;
}) {
  const leadQuery = useAsync(async () => {
    const { data, error } = await supabase
      .from("leads")
      .select("contact_id,customer_match_reviewed_at")
      .eq("job_id", job.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data as PublicLead | null;
  }, [job.id]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const source = contacts.find((contact) => contact.id === job.contact_id);
  const matches = useMemo(
    () =>
      source
        ? findDuplicates(contacts, {
            name: source.full_name,
            cell: source.cell,
            phone: source.phone,
            email: source.email,
          }).filter((contact) => contact.id !== source.id)
        : [],
    [contacts, source],
  );

  if (
    !leadQuery.data ||
    leadQuery.data.customer_match_reviewed_at ||
    leadQuery.data.contact_id !== job.contact_id ||
    job.stage !== "new_lead" ||
    matches.length === 0
  ) {
    return null;
  }

  async function confirm() {
    const match = matches.find((contact) => contact.id === selected);
    if (!match) return;
    if (
      !window.confirm(
        t(
          "Link this job to {name}? The form's phone, email, and name will NOT replace their customer details.",
          { name: match.full_name ?? t("this customer") },
        ),
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      const { error: rpcError } = await supabase.rpc(
        "confirm_public_lead_contact",
        {
          p_job_id: job.id,
          p_contact_id: match.id,
        },
      );
      if (rpcError) throw new Error(rpcError.message);
      await onLinked();
      await leadQuery.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="block" style={{ marginBottom: 16 }}>
      <h2>{t("Possible returning customer")}</h2>
      <p>
        {t(
          "This public form made a separate customer file. Check the person's identity before linking this job. Submitted contact details stay in the original lead record and will not change the existing customer.",
        )}
      </p>
      <select
        aria-label={t("Possible existing customer")}
        value={selected}
        onChange={(event) => setSelected(event.target.value)}
      >
        <option value="">{t("Choose a customer after checking")}</option>
        {matches.map((match) => (
          <option key={match.id} value={match.id}>
            {match.full_name ?? match.company ?? t("Customer")} ·{" "}
            {formatAddress(match) ||
              match.cell ||
              match.email ||
              t("No details")}
          </option>
        ))}
      </select>{" "}
      <button disabled={!selected || busy} onClick={() => void confirm()}>
        {busy ? t("Linking…") : t("Confirm and link job")}
      </button>
      <ErrorNote error={error ?? leadQuery.error} />
    </div>
  );
}
